import { Icon } from "components/Icon/Icon";
import { IconCollection } from "components/Icon/IconCollection";
import { Popover } from "components/Popover/Popover";
import { DocsInfoTooltip } from "components/Tooltip/DocsInfoTooltip";
import Tooltip from "components/Tooltip/Tooltip";
import { logsTabKey } from "containers/Resources/screens/ResourcesSingle/constants";
import PropTypes from "prop-types";
import { useState } from "react";
import "./StateTabs.scss";
const StateTabs = ({
    tabs,
    activeTab,
    setActiveTab,
    title,
    hiddenTabs,
    isConnectionType,
    isDisabled,
    replaceSearchParams,
}) => {
    // Bumped on every overflow-menu selection to force-remount the "…" Popover,
    // which resets its internal open state to closed. The Popover stays fully
    // UNCONTROLLED (open path untouched); we only reset it on select. This is
    // needed because selecting an overflow tab promotes it out of the menu, so
    // the clicked element detaches from the DOM before the Popover's inside-click
    // auto-close runs its contains() check — leaving the menu stuck open.
    const [menuInstance, setMenuInstance] = useState(0);
    const handleClick = (tab) => {
        if (activeTab === logsTabKey && tab !== activeTab) {
            replaceSearchParams?.();
        }

        setActiveTab(tab);
    };
    const isExcludedForConnection = (tab) =>
        isConnectionType &&
        (tab.uid === "script" || tab.uid === "children" || tab.uid === "savepoints");
    const filteredTabs = tabs.filter((tab) => !isExcludedForConnection(tab));

    // When the active tab lives in the overflow ("…") menu, surface it in the
    // visible tab strip and mark it selected for as long as it stays active,
    // instead of leaving no tab highlighted. It is then omitted from the overflow
    // menu so it is not shown in two places at once.
    const activeHiddenTab =
        Array.isArray(hiddenTabs) && !filteredTabs.some((tab) => tab?.uid === activeTab)
            ? hiddenTabs.find(
                  (tab) => tab?.uid === activeTab && !isExcludedForConnection(tab)
              )
            : undefined;

    const visibleTabs = activeHiddenTab ? [...filteredTabs, activeHiddenTab] : filteredTabs;
    const menuTabs = activeHiddenTab
        ? hiddenTabs.filter((tab) => tab?.uid !== activeHiddenTab.uid)
        : hiddenTabs;

    return (
        <>
            {title && (
                <p className="state-tabs__title text-uppercase text-sm text-dark-grey">{title}</p>
            )}
            <ul className="state-tabs">
                {visibleTabs.map((tab) => {
                    const buttonContent = (
                        <button
                            onClick={() => handleClick(tab?.uid)}
                            className={`${activeTab === tab?.uid ? "selected" : ""} ${
                                tab.docs ? "padding" : ""
                            }`}
                            type="button"
                            disabled={isDisabled}
                        >
                            <span>{tab?.label}</span>
                            {tab?.countHint ? (
                                <span className="state-tabs__count-hint">{tab.countHint}</span>
                            ) : null}
                        </button>
                    );
                    return (
                        <li key={`Tab-${tab?.uid}`}>
                            {isDisabled && activeTab !== tab?.uid ? (
                                <Tooltip overlay="Please save your changes in the current tab">
                                    {buttonContent}
                                </Tooltip>
                            ) : (
                                buttonContent
                            )}
                            {tab.docs && (
                                <div className="tabDocs">
                                    <DocsInfoTooltip
                                        title={tab.docs.title}
                                        urlPath={tab.docs.url}
                                    />
                                </div>
                            )}
                        </li>
                    );
                })}
                {menuTabs?.length > 0 && (
                    <Popover
                        key={menuInstance}
                        trigger={<Icon icon={IconCollection.dotsVertical} />}
                        toggleClassName="state-tabs__actions"
                        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                    >
                        <ul className="popoverMenu">
                            {menuTabs.map((tab) => (
                                <li key={tab?.uid}>
                                    <a
                                        href="#e"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            handleClick(tab?.uid);
                                            // Close the "…" menu after selecting a tab.
                                            setMenuInstance((n) => n + 1);
                                        }}
                                    >
                                        {tab?.label}
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </Popover>
                )}
                {hiddenTabs === null && (
                    <span className="state-tabs__actions">
                        <Icon icon={IconCollection.dotsVertical} />
                    </span>
                )}
            </ul>
        </>
    );
};

StateTabs.defaultProps = {
    title: null,
    hiddenTabs: [],
};

StateTabs.propTypes = {
    title: PropTypes.string,
    tabs: PropTypes.arrayOf(PropTypes.object).isRequired,
    hiddenTabs: PropTypes.arrayOf(PropTypes.object),
    activeTab: PropTypes.string.isRequired,
    setActiveTab: PropTypes.func.isRequired,
    isConnectionType: PropTypes.bool,
    isDisabled: PropTypes.bool,
    replaceSearchParams: PropTypes.func,
};

export default StateTabs;
